"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  INVESTIGATIONS,
  DATA_SETS,
  CIVIC_AREAS,
  AREA_ACCENT,
  accentVar,
} from "@/lib/sections";

/**
 * Global two-tier nav: "Investigations" and "Data" triggers, each opening a
 * panel (Data grouped by civic area). Mirrors the design's mega-nav and reads
 * the same `sections.ts` registry the home and ⌘K palette use, so the bar never
 * has to grow as sections are added — new data sets land in an area column.
 *
 * Static content only (names, questions, hrefs, accents) — no per-render data
 * load, so it's cheap on every page. Counts/figures stay on the home, which
 * loads real artefacts. Search is delegated to the global <CommandPalette> via
 * the `data-search` attribute.
 */
type Menu = "inv" | "data" | null;

export function MainNav() {
  const pathname = usePathname();
  const [menu, setMenu] = useState<Menu>(null);
  const [panelTop, setPanelTop] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  // Anchor the full-width subnav directly below the (sticky) header by
  // measuring its bottom edge whenever a menu opens.
  const open = useCallback((m: Menu) => {
    clear();
    if (m) {
      const header = wrapRef.current?.closest("header");
      if (header) setPanelTop(header.getBoundingClientRect().bottom);
    }
    setMenu(m);
  }, []);
  const scheduleClose = useCallback(() => {
    clear();
    closeTimer.current = setTimeout(() => setMenu(null), 140);
  }, []);

  // Close on outside pointer + Escape.
  useEffect(() => {
    function onDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenu(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenu(null);
    }
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Close the panel after navigating.
  //
  // Derived during render, not in an effect. An effect runs AFTER the commit,
  // so the new page painted once with the old menu still open and then
  // re-rendered to close it — a flash of the previous section's dropdown over
  // the page you just navigated to, worst on the slow devices least able to
  // hide it. Comparing the previous pathname during render is React's
  // documented way to reset state when a value changes, and it closes the
  // menu before anything paints.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setMenu(null);
  }

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(href + "/");

  const trig = (m: Exclude<Menu, null>) => ({
    "aria-haspopup": true as const,
    "aria-expanded": menu === m,
    onClick: () => (menu === m ? setMenu(null) : open(m)),
    onMouseEnter: () => open(m),
  });

  // Below sm the triggers collapse — the ⌘K palette (always visible) is the
  // mobile browse path, so the bar stays a wordmark + search on a phone.
  const trigCls = (m: Exclude<Menu, null>) =>
    `hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm transition-colors hover:bg-bg-2 ${
      menu === m ? "bg-bg-2 text-ink" : "text-ink-2"
    }`;

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center gap-0.5 sm:gap-1"
      onMouseLeave={scheduleClose}
    >
      <button className={trigCls("inv")} {...trig("inv")}>
        Investigations
        <span aria-hidden className={`text-[11px] transition-transform ${menu === "inv" ? "rotate-180" : ""}`}>▾</span>
      </button>
      <button className={trigCls("data")} {...trig("data")}>
        Data
        <span aria-hidden className={`text-[11px] transition-transform ${menu === "data" ? "rotate-180" : ""}`}>▾</span>
      </button>
      <button
        data-search
        aria-label="Search"
        className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-2 sm:py-1.5 min-h-11 sm:min-h-0 rounded-md border border-rule text-ink-3 hover:text-ink hover:border-ink-3 transition-colors"
      >
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
          <circle cx="7" cy="7" r="4.5" />
          <path d="M11 11l3.5 3.5" strokeLinecap="round" />
        </svg>
        <kbd className="cs-mono text-[11px] hidden sm:inline">⌘K</kbd>
      </button>

      {/* dropdown panel */}
      {menu && (
        <div
          className="fixed left-0 right-0 z-40"
          style={{ top: panelTop }}
          onMouseEnter={clear}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-bg-3 border-b border-rule shadow-[0_24px_48px_-30px_rgba(20,16,8,.5)] cs-reveal">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-6">
              {menu === "inv" ? (
                <>
                  <div className="cs-label text-ink-3 mb-3">Investigations — the read</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {INVESTIGATIONS.map((inv) => {
                      const accent = accentVar(inv.accent);
                      return (
                        <Link
                          key={inv.id}
                          href={inv.href}
                          className={`block rounded-lg border border-transparent p-4 hover:bg-bg-2 hover:border-rule transition-colors ${isActive(inv.href) ? "bg-bg-2" : ""}`}
                        >
                          <span className="flex items-center gap-2 cs-label">
                            <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
                            {inv.name}
                          </span>
                          <span className="block text-[16px] text-ink mt-2 leading-snug">
                            {inv.question}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="cs-label text-ink-3 mb-3">Data — the standing record</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-5">
                    {CIVIC_AREAS.map((area) => {
                      const sets = DATA_SETS.filter((d) => d.area === area);
                      if (sets.length === 0) return null;
                      const accent = accentVar(AREA_ACCENT[area]);
                      return (
                        <div key={area}>
                          <div
                            className="cs-label pb-1.5 mb-1.5 border-b"
                            style={{ color: accent, borderBottomColor: accent }}
                          >
                            {area}
                          </div>
                          {sets.map((d) => (
                            <Link
                              key={d.id}
                              href={d.href}
                              className={`block rounded-md px-2 py-2 hover:bg-bg-2 ${isActive(d.href) ? "bg-bg-2" : ""}`}
                            >
                              <span className="text-[16px] text-ink">{d.name}</span>
                              <span className="block cs-mono text-ink-3 text-[11px] normal-case tracking-normal leading-snug mt-0.5">
                                {d.blurb}
                              </span>
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
